import { MAX_LATEST_VISITORS_LIMIT } from '@va/constants';
import { getInstanceId } from '@va/dashboard/selectors/app';
import { useCurrentPeriodFilter, usePreviousPeriodFilter } from '@va/dashboard/util-hooks';
import { get, post } from '@va/http-client';
import { DeviceTypesEnum } from '@va/types/device';
import { AlarmingEvents, EventsData } from '@va/types/events';
import { Where } from '@va/types/filters';
import { RequestOrder, RequestSegment, TimeIntervalQuery } from '@va/types/requests';
import { Pagination } from '@va/types/table';
import { getFillColor, MapData } from '@va/ui/components/maps';
import { getAlpha2CountryCodes, toQueryString } from '@va/util/helpers';
import { useFetchData, useLazyDataFetch } from '@va/util/hooks';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

export type VisitorDataType = {
  countryCode: string;
  ip: string;
  visitorType: number;
  latitude: number;
  longitude: number;
  deviceType: string;
  visitorKey: string;
  platform: string;
  browserName: string;
  visitDetails: string;
  lastVisitTs: number;
  sessionKey: string;
};

export type LiveVisitorsMapDataType = MapData<{ visitors: VisitorDataType[]; count: number }>;

export type Meta = {
  page: number;
  pageSize: number;
  total: number;
};

export type PayloadItem = {
  count: number;
  previousCount?: number;
};

export type VisitsResponse = {
  meta: {
    pageVisits: number;
    previousPageVisits?: number;
  } & Meta;
  payload: Array<
    {
      id: string;
      suffix: string;
      title: string;
      url: string;
    } & PayloadItem
  >;
};

export type VisitorsResponse = {
  meta: {
    visitorsTotal: number;
    previousVisitorsTotal?: number;
  } & Meta;
  payload: Array<
    {
      [key in 'device' | 'browser' | 'display' | 'operatingSystem']: string;
    } & PayloadItem
  >;
};

type SessionByCountryPayloadItem = { country: string } & PayloadItem;

export type SessionsByCountryResponse = {
  meta: {
    sessionsTotal: number;
    previousSessionsTotal?: number;
  } & Meta;
  payload: Array<SessionByCountryPayloadItem>;
};

export type Query = {
  page: number;
  pageSize: number;
  group?: string;
  order?: string;
};

type SessionInfo = {
  visitorKey: string;
  sessionKey: string;
  ip: string;
  countryCode: string;
  deviceType: DeviceTypesEnum;
  platform: string;
  browserName: string;
  pagesVisited: number;
  timestamp: number;
  location: {
    lat: number;
    lng: number;
  };
  referrer: string;
  screenSize: string;
  status: number;
  sessionExtras: {
    id: string;
    seen: boolean;
    star: boolean;
  };
  comment: string | null;
  alarmingEvents: AlarmingEvents;
  events: Partial<EventsData>;
};

export const getSessionInfo = (websiteId: string, id: string) => {
  const url = `/websites/${websiteId}/sessions/${id}/info`;
  return get(url, {});
};

export const useSessionInfo = (sessionId: string) => {
  const websiteId = useSelector(getInstanceId);
  return useFetchData<SessionInfo>(`/websites/${websiteId}/sessions/${sessionId}/info`);
};

export type LatestVisitorsMapResponse = {
  payload: SessionLocation[];
};

export type SessionLocation = {
  sessionKey: string;
  lat: number;
  lng: number;
  ip: string;
  visitorKey: string;
  privacyLevel: string;
};

export type GetLatestVisitorsMapParams = {
  websiteId: string;
  queryParams: TimeIntervalQuery & { limit?: number };
  body: {
    where?: Where;
    order?: RequestOrder[];
    pagination?: Pagination;
    segments?: RequestSegment[];
    primaryDimension?: string;
  };
};

export const getLatestVisitorsMap = ({
  websiteId,
  queryParams: { from, until, limit },
  body,
}: GetLatestVisitorsMapParams) => {
  const url = `/v2/websites/${websiteId}/sessions/geo-location`;

  const queryData = {
    from: typeof from === 'string' ? from : from.toISOString(),
    until: typeof until === 'string' ? until : until.toISOString(),
    limit: limit ?? MAX_LATEST_VISITORS_LIMIT,
  };

  return post<LatestVisitorsMapResponse>(url, queryData, body);
};

export const getLatestVisitorsList = (
  websiteId: string,
  queryData: Record<string, unknown>,
  payloadData: Record<string, unknown>,
) => {
  const url = `/websites/${websiteId}/sessions/table`;
  return post(url, queryData, payloadData);
};

export const useVisitors = <T>(
  query: Query,
  mapper?: (x: VisitorsResponse) => Array<T>,
  shouldFetch: boolean = true,
) => {
  const { from, until, websiteId } = useCurrentPeriodFilter(false);
  const { from: previousFrom, until: previousUntil } = usePreviousPeriodFilter(false);
  const queryData = useMemo(
    () => ({ from, until, previousFrom, previousUntil, ...query }),
    [from, previousFrom, previousUntil, query, until],
  );

  const url = useMemo(() => `/v2/websites/${websiteId}/visitors?${toQueryString(queryData)}`, [queryData, websiteId]);

  return useLazyDataFetch<Array<T>, Error>(shouldFetch ? url : null, undefined, mapper);
};

export type MappedSessionByCountry = Omit<SessionByCountryPayloadItem, 'country'> & {
  percent: number;
  countryCode: string;
};

type LiveVisitor = {
  browserName: string;
  countryCode: string;
  deviceType: string;
  ip: string;
  platform: string;
  visitDetails: string;
  visitorKey: string;
  lastVisitTs: number;
  latitude: number;
  longitude: number;
  visitorType: number;
  sessionKey: string;
};

export type LiveVisitorsResponse = {
  count: number;
  visitors: Array<LiveVisitor>;
};

export type MappedLiveVisitorsResponse = {
  count: number;
  visitors: LiveVisitorsMapDataType;
};
type VisitorsCountType = {
  [countryCode: string]: number;
};

export const useLiveVisitors = () => {
  const { from, until, websiteId } = useCurrentPeriodFilter(true);

  const queryData = { from, until };

  const mapperFn = useCallback((response: LiveVisitorsResponse) => {
    const { visitors, count } = response;
    const alpha2CountryCodes = getAlpha2CountryCodes();
    const visitorsCount: VisitorsCountType = {} as VisitorsCountType;
    const formattedData = {} as LiveVisitorsMapDataType;
    visitors?.forEach((visitor) => {
      const { countryCode } = visitor;
      const alpha3countryCode = alpha2CountryCodes[countryCode];
      if (alpha3countryCode in formattedData) {
        const aux = formattedData[alpha3countryCode];

        aux.visitors.push({ ...visitor });
        if (aux?.count) {
          aux.count += 1;
        }

        visitorsCount[alpha3countryCode] = visitorsCount[alpha3countryCode] + 1;
      } else {
        formattedData[alpha3countryCode] = {
          fillColor: 'rgba(92, 4, 180,0.1)',
          visitors: [{ ...visitor }],
          count: 1,
        };
        visitorsCount[alpha3countryCode] = 1;
      }
    });

    for (const countryKey in formattedData) {
      formattedData[countryKey].fillColor = getFillColor(visitorsCount[countryKey]);
    }

    return { visitors: formattedData, count };
  }, []);

  return useLazyDataFetch<MappedLiveVisitorsResponse, Error>(
    `/websites/${websiteId}/overview/live-visitors?${toQueryString(queryData)}`,
    { keepPreviousData: true },
    mapperFn,
  );
};

export type VisitSession = {
  id: string;
  status: number;
  sessionStart: number;
  ip: string;
  countryCode: string;
  location: {
    lat: number;
    lng: number;
  };
  adCampaignKey: string | null;
  adCampaignSource: string | null;
  adCampaignMedium: string | null;
  adCampaignTerm: string | null;
  adCampaignContent: string | null;
  referrer: string;
  hasRecording: boolean;
  duration: number;
  adCampaignLabel: string;
  pageVisits: {
    key: string;
    url: string;
    title: string;
    unixTs: number;
    privacyLevel: number;
  }[];
  browser: string;
  platform: string;
  device: DeviceTypesEnum;
  display: string;
};

export type VisitorHistoryResponse = {
  data: {
    ip: string;
    device: DeviceTypesEnum;
    platform: string;
    browser: string;
    countryCode: string;
    display: string;
    location: {
      lat: number;
      lng: number;
    };
    sessions: VisitSession[];
    sessionExtras: {
      id: string;
      seen: boolean;
      star: boolean;
    };
  };
};

export const useVisitorsHistory = (sessionKey: string) => {
  const websiteId = useSelector(getInstanceId);
  const queryData = {
    sessionId: sessionKey,
  };

  const mapperFn = useCallback((response: VisitorHistoryResponse) => {
    const {
      data: { sessions, browser, device, platform, display },
    } = response;
    return sessions.map((session) => {
      return { ...session, browser, device, platform, display };
    });
  }, []);

  return useFetchData<VisitSession[], Error>(
    `/websites/${websiteId}/sessions/log?${toQueryString(queryData)}`,
    undefined,
    mapperFn,
  );
};
